import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import TranslatedText from '../i18n/TranslatedText';
import GameAnalytics from "../components/analytics/GameAnalytics";


const InstallAppModal = ({ appState, day }) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [initialWaitComplete, setInitialWaitComplete] = useState(false);

  // Simplified helper to check if enough time has passed since last dismissal
  const canShowModal = () => {
    const lastDismissed = localStorage.getItem('installModalDismissed');
    return !lastDismissed || 
      (Date.now() - parseInt(lastDismissed, 10)) > 24 * 60 * 60 * 1000;
  };

  // Initial device check and modal show logic
  useEffect(() => {
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isAndroid = /Android/.test(navigator.userAgent);
    const isStandalone = window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches;

    setIsIOS(iOS);

    if ((iOS || isAndroid) && !isStandalone && canShowModal()) {
      // Set initial timeout flag
      const timeoutRef = setTimeout(() => {
        setInitialWaitComplete(true);
      }, 30000);
  
      // Cleanup timeout on unmount or appState change
      return () => clearTimeout(timeoutRef);
    }
  }, []); // Run only once on mount
  
  // Separate effect to handle modal display
  useEffect(() => {
    if (!initialWaitComplete) return;
    
    // Only show on WELCOME state
    if ( ( appState === 'WELCOME' || appState === 'DAY_BREAK' )  && day >= 3 && canShowModal()) {
      setShowModal(true);
    } else {
      setShowModal(false);
    }
  }, [appState, day, initialWaitComplete]);


  const handleDismiss = () => {
    localStorage.setItem('installModalDismissed', Date.now().toString());
    setShowModal(false);
    GameAnalytics.trackDismissAppModal();
  };

  const ShareIcon = () => isIOS ? (
    // iOS share icon
    <svg className="w-6 h-6 text-stone-800" viewBox="0 0 24 24" fill="none">
      <path d="M13 5.41V17a1 1 0 0 1-2 0V5.41l-3.3 3.3a1 1 0 0 1-1.4-1.42l5-5a1 1 0 0 1 1.4 0l5 5a1 1 0 1 1-1.4 1.42L13 5.4z" fill="currentColor"/>
      <path d="M3 12a1 1 0 0 1 2 0v7a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-7a1 1 0 1 1 2 0v7a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3v-7z" fill="currentColor"/>
    </svg>
  ) : (
    // Android share icon
    <svg className="w-6 h-6 text-stone-800" viewBox="0 0 24 24" fill="none">
      <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z" fill="currentColor"/>
    </svg>
  );

  const AddToHomeIcon = () => isIOS ? (
    // iOS Add to Home Screen icon
    <svg className="w-6 h-6 text-stone-800" viewBox="0 0 24 24" fill="none">
      <rect x="2" y="2" width="20" height="20" rx="5" stroke="currentColor" strokeWidth="2"/>
      <path d="M12 8V16M8 12H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ) : (
    // Android Add to Home Screen icon
    <svg className="w-6 h-6 text-stone-800" viewBox="0 0 24 24" fill="none">
      <path d="M18 1.01L8 1c-1.1 0-2 .9-2 2v3h2V5h10v14H8v-1H6v3c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99z" fill="currentColor"/>
      <path d="M11 7H5v2h6v2.59L7.71 15.9l1.42 1.41L15 11.41l-5.88-5.89L7.7 6.93 11 10.33V7z" fill="currentColor"/>
    </svg>
  );

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50">
      <div className="fixed inset-x-0 bottom-0 z-50 p-4 pb-8 md:p-6 md:pb-10">
        <Card className="w-full max-w-2xl mx-auto bg-white shadow-[0_0_15px_rgba(0,0,0,0.1)]">
          <CardHeader className="flex flex-row items-center justify-between pb-3 space-y-0">
            <CardTitle className="text-xl">{t('install.title')}</CardTitle>
            <Button 
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0 mt-0"
              onClick={handleDismiss}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">{t('install.close')}</span>
            </Button>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-6">
              <img 
                src="/logo192.png" 
                alt="App Icon" 
                className="w-12 h-12 rounded-lg"
              />
              <div>
                <p className="font-medium">{t('install.apptitle')}</p>
                <p className="text-sm text-stone-500">{t('install.domain')}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  <ShareIcon />
                </div>
                <span className="text-sm">
                  <TranslatedText content={t('install.steps.shareButton', { 
                  button: t(isIOS ? 'install.steps.ios.button' : 'install.steps.android.button')
                })} />
                </span>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  <AddToHomeIcon />
                </div>
                <span className="text-sm">
                <TranslatedText content={t('install.steps.installButton', {
                  button: t(isIOS ? 'install.steps.ios.action' : 'install.steps.android.action')
                })} />
                </span>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  <img src="/logo192.png" alt="App Icon" className="w-6 h-6 rounded-lg" />
                </div>
                <span className="text-sm">
                <TranslatedText content={t('install.steps.findApp', { app: 'Bake Rush' })} />
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InstallAppModal;